import { DEFAULT_PALETTE } from '../../core/palette';
import type { ParamSchema, SchemaValues } from '../../core/params/types';

export const GLYPH_PARTICLES_PARAMS = [
  {
    kind: 'int',
    key: 'count',
    label: 'Partikel',
    min: 20,
    max: 20000,
    step: 1,
    scale: 'log',
    default: 900,
    bulkRange: [150, 4000],
    group: 'Emission',
  },
  {
    kind: 'int',
    key: 'steps',
    label: 'Simulationsschritte',
    min: 1,
    max: 1200,
    step: 1,
    default: 130,
    bulkRange: [20, 400],
    group: 'Emission',
  },
  {
    kind: 'enum',
    key: 'origin',
    label: 'Emissionsquelle',
    default: 'outline',
    group: 'Emission',
    options: [
      { value: 'outline', label: 'Textkontur' },
      { value: 'inside', label: 'Im Text' },
      { value: 'random', label: 'Zufällig' },
      { value: 'edges', label: 'Canvas-Rand' },
    ],
  },
  {
    kind: 'enum',
    key: 'source',
    label: 'Zeichenquelle',
    default: 'ownText',
    group: 'Emission',
    options: [
      { value: 'ownText', label: 'Eigener Text' },
      { value: 'alphabet', label: 'Alphabet' },
      { value: 'digits', label: 'Zahlen' },
      { value: 'symbols', label: 'Symbole' },
      { value: 'custom', label: 'Eigene Zeichen' },
    ],
  },
  {
    kind: 'text',
    key: 'customChars',
    label: 'Eigene Zeichen',
    default: '◆◇○●△▽',
    group: 'Emission',
    showIf: (v) => v.source === 'custom',
    bulkValues: ['◆◇○●△▽', '@#$%&*', '←↑→↓', '⌁⌂⌘⌥'],
  },

  {
    kind: 'float',
    key: 'stepSize',
    label: 'Schrittweite',
    min: 0.1,
    max: 20,
    step: 0.1,
    default: 2.2,
    unit: 'px',
    group: 'Bewegung',
  },
  {
    kind: 'float',
    key: 'noiseScale',
    label: 'Noise-Skala',
    min: 0.0004,
    max: 0.02,
    step: 0.0001,
    scale: 'log',
    default: 0.0026,
    precision: 4,
    unit: '1/px',
    group: 'Bewegung',
  },
  {
    kind: 'float',
    key: 'gravity',
    label: 'Schwerkraft',
    min: -1,
    max: 1,
    step: 0.01,
    default: 0.12,
    group: 'Bewegung',
  },
  {
    kind: 'float',
    key: 'textRepel',
    label: 'Textabstossung',
    min: -4,
    max: 4,
    step: 0.05,
    default: 1.4,
    group: 'Bewegung',
    tooltip: 'Negativ zieht die Zeichen zum Text hin.',
  },
  {
    kind: 'float',
    key: 'textRadius',
    label: 'Reichweite',
    min: 5,
    max: 600,
    step: 1,
    scale: 'log',
    default: 130,
    unit: 'px',
    group: 'Bewegung',
  },

  {
    kind: 'range',
    key: 'size',
    label: 'Zeichengrösse',
    min: 2,
    max: 200,
    step: 0.5,
    default: [8, 26],
    unit: 'px',
    precision: 1,
    group: 'Darstellung',
  },
  {
    kind: 'bool',
    key: 'rotateToMotion',
    label: 'Rotation folgt der Bewegung',
    default: true,
    group: 'Darstellung',
  },
  {
    kind: 'float',
    key: 'rotationJitter',
    label: 'Rotationsjitter',
    min: 0,
    max: 180,
    step: 1,
    default: 18,
    unit: '°',
    group: 'Darstellung',
  },
  {
    kind: 'float',
    key: 'opacity',
    label: 'Deckkraft',
    min: 0.02,
    max: 1,
    step: 0.01,
    default: 0.85,
    group: 'Darstellung',
  },
  {
    kind: 'bool',
    key: 'trail',
    label: 'Spur zeichnen',
    default: false,
    group: 'Darstellung',
    tooltip: 'Zeichnet an jedem Schritt ein Zeichen statt nur am Endpunkt.',
  },
  {
    kind: 'int',
    key: 'trailEvery',
    label: 'Spur-Abstand',
    min: 1,
    max: 40,
    step: 1,
    default: 6,
    group: 'Darstellung',
    showIf: (v) => v.trail === true,
  },

  {
    kind: 'enum',
    key: 'colorMode',
    label: 'Farbmodus',
    default: 'distance',
    group: 'Farbe',
    options: [
      { value: 'solid', label: 'Einfarbig' },
      { value: 'distance', label: 'Nach Textdistanz' },
      { value: 'life', label: 'Nach Lebensdauer' },
      { value: 'random', label: 'Zufällig' },
    ],
  },
  {
    kind: 'palette',
    key: 'palette',
    label: 'Palette',
    default: { ...DEFAULT_PALETTE, paletteId: 'acid' },
    group: 'Farbe',
  },
] as const satisfies ParamSchema;

export type GlyphParticlesValues = SchemaValues<typeof GLYPH_PARTICLES_PARAMS>;
